import { v4 as uuid } from "uuid";
import { forkJoin, mergeMap, of } from "rxjs";
import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import * as FlatStates from '@medsurf/flat-states';
import * as FlatFacades from '../../internal';

@Injectable()
export class PoiEntityFacade {
  constructor(protected store: Store) {
  }

  //<editor-fold desc="Selectors">

  //</editor-fold>

  //<editor-fold desc="Requests">
  
  static addPoi(store: Store,
                id: string,
                page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                image: FlatModels.ImageEntityModels.Image | null,
                currentPositionAndZoom: FlatModels.AnnotationEntityModels.PositionAndZoom,
                text: string | null,
                clusterParentUpdate = false) {
    // Create New Poi
    const newPoi: FlatModels.PointOfInterestEntityModels.PointOfInterest = {
      id: uuid(),
      order: page.pois?.length || 0,
      name: text || 'Neuer Poi',
      note: '',
      bounds: {
        x: currentPositionAndZoom.currentBounds.x,
        y: currentPositionAndZoom.currentBounds.y,
        width: currentPositionAndZoom.currentBounds.width,
        height: currentPositionAndZoom.currentBounds.height,
      },
      annotationToPointOfInterest: [],
      slide: page.id,
      images: image ? [image.id] : []
    }
    
    // Update Image
    if (image) {
      image.pois.push(newPoi.id);
    }
    
    // Update Page
    page.pois.push(newPoi.id);
    
    // Add Poi And Update Page
    let layer: FlatModels.LayerEntityModels.Layer;
    return store.dispatch(new FlatActions.PointOfInterestEntityActions.AddEntity(newPoi)).pipe(
      mergeMap((data) => {
        if (image) {
          return store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))
        }
        return of(data);
      }),
      mergeMap(() =>
        store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page))
      ),
      mergeMap((result) => {
        if (image) {
          const typedLayers = store.selectSnapshot(FlatStates.SlideLayerEntityState.typedEntities$);
          layer = typedLayers[image.layer];
          if (layer) {
            return store.dispatch(new FlatActions.SlideLayerEntityActions.SetLayerIndex(layer.position));
          }
        }
        return of(result);
      }),
      mergeMap((result) => {
        if (image && layer) {
          const index = layer.images.indexOf(image.id);
          if (index > -1) {
            return store.dispatch(new FlatActions.ImageEntityActions.SetImageIndex(index))
          }
        }
        return of(result);
      }),
      mergeMap((data) => {
        store.dispatch(new FlatActions.PointOfInterestEntityActions.SetPoiId(newPoi.id));
        store.dispatch(new FlatActions.SlideAuthorActions.SetEditPanelDisplay(FlatModels.SlideAuthorModels.EditPanelDisplay.enum.poi));
        store.dispatch(new FlatActions.SlideAuthorActions.ScrollSlideTreeElementIntoView(newPoi.id));
        return of(data);
      }),
    );
  }
  
  protected static copyPoi(store: Store,
                           mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                           id: string,
                           page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                           images: FlatModels.ImageEntityModels.Image[] | null, // TODO remove this -> do it in copyPois
                           forceImageUpdate: boolean,
                           typedImagePois: {[id: string]: string[]},
                           withAnnotations: boolean,
                           clusterParentUpdate = false) {
    // Check If Poi Mapping Exits
    if (mappings.poi[id]) {
      return of(undefined);
    }
    
    // Get Data
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    
    // Get Poi
    const poi = structuredClone(typedPois[id]);
    
    // Update Poi Mappings
    mappings.poi[id] = uuid();
    
    // Update Poi
    poi.id = mappings.poi[id];
    poi.slide = page.id;
    poi.order = page.pois?.length || 0;
    
    // Update Image
    if (forceImageUpdate) {
      // Update Image
      poi.images = (poi.images || []).reduce((arr, image) => {
        // Get Mappings
        const mapping = mappings.image[image];
        if (mapping) {
          arr.push(mapping);
        }
        
        if (typedImagePois[mapping]) {
          typedImagePois[mapping].push(poi.id);
        } else {
          typedImagePois[mapping] = [poi.id];
        }
        
        return arr;
      }, [] as string[]);
    } else {
      poi.images = [];
      (images || []).forEach((image) => {
        if (poi.images) {
          poi.images.push(image.id);
        } else {
          poi.images = [image.id];
        }
        image.pois.push(poi.id);
      });
    }
    
    // Update Page
    page.pois.push(poi.id);
    
    // Annotation Fix
    if (withAnnotations) {
      const annotationToPointOfInterset = structuredClone(poi.annotationToPointOfInterest);
      poi.annotationToPointOfInterest = annotationToPointOfInterset.reduce((arr, atpoi) => {
        const mapping = mappings.annotation[atpoi.annotation];
        if (mapping) {
          arr.push({
            id: uuid(),
            pointOfInterest: poi.id,
            annotation: mapping,
            order: atpoi.order || 0
          });
        }
        return arr;
      }, [] as FlatModels.PointOfInterestEntityModels.AnnotationToPointOfInterest[]);
    } else {
      poi.annotationToPointOfInterest = [];
    }
    
    // Copy Poi
    return of(poi.id).pipe(
      mergeMap(() =>
        store.dispatch(new FlatActions.PointOfInterestEntityActions.AddEntity(poi))
      ),
      mergeMap((data) => {
        if (!forceImageUpdate) {
          if (clusterParentUpdate) {
            return of(data);
          }
        }
        if (!images || images.length === 0) {
          return of(data);
        }
        return forkJoin(images.map(image => store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image))));
      }),
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of(data);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page));
      }),
      mergeMap(() => of(poi.id))
    )
  }
  
  static copyPois(store: Store,
                  mappings: FlatModels.BulkEditAuthorModels.BulkEditMapping,
                  ids: string[],
                  page: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training,
                  images: FlatModels.ImageEntityModels.Image[] | null,
                  withAnnotations: boolean,
                  clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of([]);
    }
    
    // Sort Pois
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$)
    const sortedIds = ids.reduce((arr, id) => {
      const entity = typedPois[id];
      if (entity) {
        arr.push(entity);
      }
      return arr;
    }, [] as FlatModels.PointOfInterestEntityModels.PointOfInterest[])
    .sort((a, b) => a.order - b.order)
    .map((poi) => poi.id);
    
    // TODO ---------------------------------------------------------
    const typedImagePois: {[id: string]: string[]} = {};
    const forceImageUpdate = images === null;
    
    // Copy Pois
    return forkJoin(sortedIds.map(id => PoiEntityFacade.copyPoi(store, mappings, id, page, images, forceImageUpdate, typedImagePois, withAnnotations, true))).pipe(
      mergeMap((newIds) => {
        const selectIds = newIds.reduce((arr, id) => {
          if (id !== undefined) {
            arr.push(id);
          }
          return arr;
        }, [] as string[]);
        return of(selectIds);
      }),
      mergeMap((selectIds) => {
        if (!forceImageUpdate) {
          if (clusterParentUpdate || !images || images.length === 0) {
            return of(selectIds);
          }
        } else {
          // Get Typed Images
          const typedImages = store.selectSnapshot(FlatStates.ImageEntityState.typedEntities$);
          images = Object.keys(typedImagePois).reduce((arr, imageId) => {
            const entity = structuredClone(typedImages[imageId]);
            if (entity) {
              const poiSet = new Set<string>(entity.pois);
              for (const poiId of typedImagePois[imageId]) {
                poiSet.add(poiId);
              }
              entity.pois = Array.from(poiSet);
              arr.push(entity);
            }
            return arr;
          }, [] as FlatModels.ImageEntityModels.Image[]);
        }
        return forkJoin(images.map(image => store.dispatch(new FlatActions.ImageEntityActions.UpdateEntity(image)))).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
      mergeMap((selectIds) => {
        if (clusterParentUpdate) {
          return of(selectIds);
        }
        return store.dispatch(new FlatActions.PageEntityActions.UpdateEntity(page)).pipe(
          mergeMap(() => of(selectIds))
        );
      }),
    )
  }

  static removePoi(store: Store,
                   id: string,
                   clusterParentUpdate = false) {
    // Get Data
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);

    // Get Poi
    const poi = structuredClone(typedPois[id]);
    
    // Create Poi Pipe Object
    const poiPipeModel: FlatModels.PipeEntityModels.PointOfInterestPipeObject = {
      data: {},
      poi,
    };
    
    // Remove Poi
    return of(poiPipeModel).pipe(
      mergeMap((result) =>
        store.dispatch(new FlatActions.BulkEditAuthorActions.RemovePoi(id)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result) =>
        store.dispatch(new FlatActions.PointOfInterestEntityActions.RemoveEntity(poi)).pipe(
          mergeMap((data) => of(Object.assign(result, {data: data}))),
        )
      ),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        if ((poi.images || []).length > 0) {
          return forkJoin((poi.images || []).map(image => FlatFacades.ImageEntityFacade.removePoiFromImage(store, poi.id, image))).pipe(
            mergeMap((data) => of(Object.assign(result, {data: data})))
          );
        }
        return of(result);
      }),
      mergeMap((result) => {
        if (clusterParentUpdate) {
          return of(result);
        }
        if (poi.slide) {
          return FlatFacades.PageEntityFacade.removePoiFromPage(store, poi.id, poi.slide).pipe(
            mergeMap((data) => of(Object.assign(result, {data: data})))
          );
        }
        return of(result);
      }),
    );
  }

  static removePois(store: Store,
                    ids: string[]) {
    if (ids.length === 0) {
      return of([]);
    }

    // Remove Pois
    return of({}).pipe(
      mergeMap(() =>
        forkJoin(ids.map(id => PoiEntityFacade.removePoi(store, id, true)))
      ),
      mergeMap((pois: FlatModels.PipeEntityModels.PointOfInterestPipeObject[]) => {
        const typedPoiImages = pois.reduce((arr, pointOfInterestPipeObject) => {
          for (const image of pointOfInterestPipeObject.poi.images || []) {
            const entity = arr[image];
            if (entity) {
              arr[image].pois.push(pointOfInterestPipeObject.poi.id);
            } else {
              arr[image] = {image, pois: [pointOfInterestPipeObject.poi.id]};
            }
          }
          return arr;
        }, {} as {[id: string]: {image: string, pois: string[]}});
        const poiImages = Object.values(typedPoiImages);
        if (poiImages.length > 0) {
          return forkJoin(poiImages.map(poiImage => FlatFacades.ImageEntityFacade.removePoisFromImage(store, poiImage.pois, poiImage.image))).pipe(
            mergeMap(() => {
              return of(pois)
            }),
          );
        }
        return of(pois);
      }),
      mergeMap((pois: FlatModels.PipeEntityModels.PointOfInterestPipeObject[]) => {
        const typedPoiPages = pois.reduce((arr, pointOfInterestPipeObject) => {
          const page = pointOfInterestPipeObject.poi.slide;
          const entity = arr[page];
          if (entity) {
            arr[page].pois.push(pointOfInterestPipeObject.poi.id);
          } else {
            arr[page] = {page, pois: [pointOfInterestPipeObject.poi.id]};
          }
          return arr;
        }, {} as {[id: string]: {page: string, pois: string[]}});
        const poiPages = Object.values(typedPoiPages);
        if (poiPages.length > 0) {
          return forkJoin(poiPages.map(poiPage => FlatFacades.PageEntityFacade.removePoisFromPage(store, poiPage.pois, poiPage.page))).pipe(
            mergeMap(() => {
              return of(pois)
            }),
          );
        }
        return of(pois);
      }),
    );
  }
  
  static removeAnnotationFromPoi(store: Store,
                                 id: string,
                                 poiId: string,
                                 clusterParentUpdate = false) {
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    const poi = structuredClone(typedPois[poiId]);
    
    // Check Index
    const index = poi.annotationToPointOfInterest.findIndex((atpoi) => atpoi.annotation === id && atpoi.pointOfInterest === poi.id);
    if (index < 0) {
      return of({poi});
    }
    
    // Update Poi
    poi.annotationToPointOfInterest.splice(index, 1);
    
    // Remove Annotation From Poi
    return of({poi}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations(poi, poi.annotationToPointOfInterest));
      }),
    );
  }
  
  static removeAnnotationsFromPoi(store: Store,
                                  ids: string[],
                                  poiId: string,
                                  clusterParentUpdate = false) {
    if (ids.length === 0) {
      return of({poiId});
    }
    
    const typedPois = store.selectSnapshot(FlatStates.PointOfInterestEntityState.typedEntities$);
    const poi = structuredClone(typedPois[poiId]);
    
    for (const id of ids) {
      // Check Index
      const index = poi.annotationToPointOfInterest.findIndex((atpoi) => atpoi.annotation === id && atpoi.pointOfInterest === poi.id);
      if (index > -1) {
        // Update Poi
        poi.annotationToPointOfInterest.splice(index, 1);
      }
    }
    
    // Remove Annotation From Poi
    return of({poi}).pipe(
      mergeMap((data) => {
        if (clusterParentUpdate) {
          return of({data});
        }
        return store.dispatch(new FlatActions.PointOfInterestEntityActions.RearrangePoiAnnotations(poi, poi.annotationToPointOfInterest));
      }),
    );
  }

  //</editor-fold>
}
